import { format } from 'date-fns';
import {
  CalendarRange,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Coins,
  Folder,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

import { cn } from '@shared';
import { formatCurrency } from '@/utils/formatters';
import { parseLocalDate } from '@/utils/date';

export type TaskDetailsSummaryItemId =
  | 'project'
  | 'members'
  | 'dates'
  | 'duration'
  | 'hourly-rate'
  | 'billing';

interface TaskDetailsSummaryProps {
  projectId: string;
  memberIds: string[];
  date: string;
  endDate?: string;
  duration: string;
  hourlyRate: number;
  currency: 'BRL' | 'USD';
  billingStatus: 'unbilled' | 'billed';
  projects: Array<{ id: string; name: string }>;
  members: Array<{ id: string; name: string }>;
  renderItem?: (
    item: TaskDetailsSummaryItem,
    trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>,
  ) => ReactNode;
}

export interface TaskDetailsSummaryItem {
  id: TaskDetailsSummaryItemId;
  label: string;
  value: string;
  ariaLabel: string;
  icon: LucideIcon;
  valueClassName?: string;
}

export function TaskDetailsSummary({
  projectId,
  memberIds,
  date,
  endDate,
  duration,
  hourlyRate,
  currency,
  billingStatus,
  projects,
  members,
  renderItem,
}: TaskDetailsSummaryProps) {
  const projectName = projects.find((project) => project.id === projectId)?.name ?? 'No project';
  const memberNames = members
    .filter((member) => memberIds.includes(member.id))
    .map((member) => member.name)
    .join(', ');
  const formattedDates = formatDateRange(date, endDate);
  const items: TaskDetailsSummaryItem[] = [
    {
      id: 'project',
      label: 'Project',
      value: projectName,
      ariaLabel: `Project: ${projectName}`,
      icon: Folder,
    },
    {
      id: 'members',
      label: 'Members',
      value: memberNames || 'No members',
      ariaLabel: `Members: ${memberIds.length} selected`,
      icon: Users,
    },
    {
      id: 'dates',
      label: 'Dates',
      value: formattedDates,
      ariaLabel: `Dates: ${formattedDates}`,
      icon: CalendarRange,
    },
    {
      id: 'duration',
      label: 'Duration',
      value: duration || 'Not set',
      ariaLabel: `Duration: ${duration || 'not set'}`,
      icon: Clock3,
    },
    {
      id: 'hourly-rate',
      label: 'Hourly rate',
      value: `${formatCurrency(hourlyRate, currency)}/hr`,
      ariaLabel: `Hourly rate: ${currency} ${hourlyRate.toFixed(2)}`,
      icon: Coins,
    },
    {
      id: 'billing',
      label: 'Billing',
      value: billingStatus === 'billed' ? 'Billed' : 'Unbilled',
      ariaLabel: `Billing: ${billingStatus === 'billed' ? 'Billed' : 'Unbilled'}. Click to mark ${billingStatus === 'billed' ? 'unbilled' : 'billed'}`,
      icon: CircleDollarSign,
      valueClassName: billingStatus === 'billed' ? 'text-emerald-700 dark:text-emerald-300' : undefined,
    },
  ];

  return (
    <section
      aria-labelledby="task-details-summary-label"
      data-testid="task-details-summary"
      className="space-y-2"
    >
      <h3 id="task-details-summary-label" className="px-1 text-sm font-semibold">
        At a glance
      </h3>
      <div className="overflow-hidden rounded-lg border bg-border">
        <div className="grid grid-cols-2 gap-px sm:grid-cols-3">
          {items.map((item) => {
            const { id, label, value, ariaLabel, icon: Icon, valueClassName } = item;
            const trigger = (
              <button
                type="button"
                data-testid={`task-summary-${id}`}
                aria-label={ariaLabel}
                className="group relative flex min-w-0 gap-2.5 bg-background p-3 text-left outline-none transition-colors hover:bg-accent/30 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring sm:gap-3 sm:p-4"
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary sm:h-[18px] sm:w-[18px]" aria-hidden="true" />
                <span className="min-w-0 pr-5">
                  <span className="block text-xs font-semibold text-muted-foreground">{label}</span>
                  <span className={cn('mt-1 block break-words text-sm font-medium leading-5', valueClassName)}>
                    {value}
                  </span>
                </span>
                <ChevronRight
                  className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-hover:text-primary group-focus-visible:text-primary sm:right-3"
                  aria-hidden="true"
                />
              </button>
            );

            return (
              <span key={id} className="contents">
                {renderItem ? renderItem(item, trigger) : trigger}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function formatDateRange(start: string, end?: string): string {
  const startDate = toDate(start);
  if (!startDate) return 'No start date';

  const endDate = toDate(end);
  if (!endDate || isSameDay(startDate, endDate)) return format(startDate, 'MMM d, yyyy');

  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`;
  }

  return `${format(startDate, 'MMM d, yyyy')} – ${format(endDate, 'MMM d, yyyy')}`;
}

function toDate(value?: string): Date | null {
  if (!value) return null;
  try {
    const date = parseLocalDate(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
